import { T, tt } from '@agos/shared'
import { useCallback, useEffect, useRef, useState } from 'react'

import { SCRATCH_THRESHOLD, scratchPercent, scratchedEnough, scratchedPoints } from './minigame'

/**
 * The scratch card, rubbed rather than pressed — §9.265, owner O7.
 *
 * *"stierací los, make sure you will not see 'Hrať', but it can be playable automatically, like in old
 * system... Do not do it like that."*
 *
 * **Legacy's card is a canvas** (`MojProfil.tsx:300–400`): a grey panel reading *Zoškrabajte tu!* that
 * erases under the pointer, and at **60 %** uncovered it submits itself. There is no button anywhere
 * on it. AGOS had a *Hrať* button, which is the same wager with the play taken out of it.
 *
 * **The threshold is the trigger and the guard.** `POST /mini-game/play` awards points and mints a
 * coupon, so it must fire exactly once: `played` latches before the request goes out, and the pointer
 * handlers stop the moment it does. A second submission would be a second prize.
 */
export function ScratchCard({
  disabled,
  onReveal,
}: {
  /** True while a play is in flight, or once this card has been used. */
  disabled: boolean
  onReveal: () => void
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const scratched = useRef<Set<string>>(new Set())
  const drawing = useRef(false)
  const played = useRef(false)
  const [percent, setPercent] = useState(0)

  // The foil. Redrawn only when the canvas appears — repainting it on every rub would erase the
  // customer's own scratching, which is the one thing this element remembers.
  useEffect(() => {
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')
    if (canvas === null || context === null || context === undefined) return
    context.fillStyle = '#9ca3af'
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.font = 'bold 16px sans-serif'
    context.fillStyle = '#6b7280'
    context.textAlign = 'center'
    context.fillText(tt('Zoškrabajte tu!'), canvas.width / 2, canvas.height / 2 - 10)
    context.fillText('✨', canvas.width / 2, canvas.height / 2 + 15)
  }, [])

  const rub = useCallback(
    (pointX: number, pointY: number) => {
      const canvas = canvasRef.current
      const context = canvas?.getContext('2d')
      if (canvas === null || context === null || context === undefined) return
      if (played.current) return

      context.globalCompositeOperation = 'destination-out'
      context.beginPath()
      context.arc(pointX, pointY, 22, 0, Math.PI * 2)
      context.fill()

      for (const point of scratchedPoints(pointX, pointY)) scratched.current.add(point)
      const uncovered = scratchPercent(scratched.current.size, canvas.width, canvas.height)
      setPercent(uncovered)

      if (scratchedEnough(uncovered) && !played.current) {
        // Latched before the call, not after it: two rubs in the same frame would otherwise both
        // pass the test and the customer would be paid twice.
        played.current = true
        onReveal()
      }
    },
    [onReveal],
  )

  const positionOf = (event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (canvas === null) return null
    const box = canvas.getBoundingClientRect()
    const scaleX = canvas.width / box.width
    const scaleY = canvas.height / box.height
    const point = 'touches' in event ? event.touches[0] : event
    if (point === undefined) return null
    return { x: (point.clientX - box.left) * scaleX, y: (point.clientY - box.top) * scaleY }
  }

  return (
    <div className="space-y-1" data-scratch-card>
      <canvas
        ref={canvasRef}
        width={320}
        height={96}
        data-scratch-canvas
        data-scratch-percent={percent}
        aria-label={tt('Stierací los - zoškrabte ho')}
        className="w-full h-24 rounded-xl touch-none cursor-pointer"
        onMouseDown={(event) => {
          if (disabled) return
          drawing.current = true
          const spot = positionOf(event)
          if (spot !== null) rub(spot.x, spot.y)
        }}
        onMouseMove={(event) => {
          if (disabled || !drawing.current) return
          const spot = positionOf(event)
          if (spot !== null) rub(spot.x, spot.y)
        }}
        onMouseUp={() => {
          drawing.current = false
        }}
        onMouseLeave={() => {
          drawing.current = false
        }}
        onTouchStart={(event) => {
          if (disabled) return
          drawing.current = true
          const spot = positionOf(event)
          if (spot !== null) rub(spot.x, spot.y)
        }}
        onTouchMove={(event) => {
          if (disabled || !drawing.current) return
          const spot = positionOf(event)
          if (spot !== null) rub(spot.x, spot.y)
        }}
        onTouchEnd={() => {
          drawing.current = false
        }}
      />
      {/* Said rather than left to be discovered: a canvas that does nothing until 60 % is a card
        * somebody gives up on at 40 %. Legacy does not say it, and legacy's card is bigger. */}
      {percent < SCRATCH_THRESHOLD && (
        <p className="text-[11px] text-muted-foreground" data-scratch-hint>
          <T>Zoškrabte los prstom alebo myšou - výhra sa odhalí sama.</T>
        </p>
      )}
    </div>
  )
}
